import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ClipboardList, Plus, Loader2, CloudOff, RefreshCw, CalendarClock,
  MapPin, Camera, ChevronRight, X, AlertTriangle, Globe,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import cultivarService from '../../services/cultivar';
import { useOrgTimezone, formatLocalDateInTz } from '@/utils/timezone';
import evaluationQueue, { type PendingEvaluation, type EvalQueueStatus } from '../../services/evaluationQueue';
import EvaluationForm from './EvaluationForm';
import {
  SEASON_STAGE_LABELS,
  type CultivarEvaluation, type EvaluationFollowup,
  type Hemisphere, type HarvestReferences,
} from '../../types/cultivar';

const HEMISPHERE_LABELS: Record<Hemisphere, string> = { S: 'Southern', N: 'Northern' };

// Evaluation sessions for one cultivar: what's queued locally, what's due for
// a re-check, and what's already been recorded.

interface Props {
  cultivarId: number;
  cultivarName: string;
}

function seasonPositionLabel(e: CultivarEvaluation): string | null {
  if (e.days_from_harvest == null) return null;
  const d = e.days_from_harvest;
  if (d === 0) return 'at harvest';
  return d < 0 ? `${Math.abs(d)}d pre-harvest` : `${d}d post-harvest`;
}

export function EvaluationsPanel({ cultivarId, cultivarName }: Props) {
  const orgTz = useOrgTimezone();
  const [evaluations, setEvaluations] = useState<CultivarEvaluation[]>([]);
  const [followups, setFollowups] = useState<EvaluationFollowup[]>([]);
  const [pending, setPending] = useState<PendingEvaluation[]>([]);
  const [queueStatus, setQueueStatus] = useState<EvalQueueStatus>(evaluationQueue.getStatus());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [focusAttributeIds, setFocusAttributeIds] = useState<number[]>([]);
  const [references, setReferences] = useState<HarvestReferences | null>(null);
  // Null = show dates exactly as recorded. Setting a hemisphere additionally
  // shows each evaluation's equivalent date there, so visits from opposite
  // sides of the equator can be compared like for like.
  const [normaliseTo, setNormaliseTo] = useState<Hemisphere | null>(null);

  const loadPending = useCallback(async () => {
    try {
      setPending(await evaluationQueue.listPending(cultivarId));
    } catch {
      /* the local queue view is advisory — never block the page on it */
    }
  }, [cultivarId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ev, fu, refs] = await Promise.all([
        cultivarService.listEvaluations(cultivarId, normaliseTo ? { normalise_to: normaliseTo } : undefined),
        cultivarService.listFollowups({ cultivar_id: cultivarId, status: 'pending' }),
        cultivarService.getHarvestReferences(cultivarId),
      ]);
      if (ev.success && ev.data) setEvaluations(ev.data);
      if (fu.success && fu.data) setFollowups(fu.data);
      if (refs.success && refs.data) setReferences(refs.data);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || 'Failed to load evaluations');
    } finally {
      setLoading(false);
    }
    await loadPending();
  }, [cultivarId, loadPending, normaliseTo]);

  useEffect(() => { load(); }, [load]);

  // Auto-sync is started here rather than in App.tsx so the IndexedDB
  // connection is only opened for users who actually work with evaluations.
  // Deliberately not stopped on unmount — a queued visit should keep uploading
  // after the user navigates away from the cultivar page.
  useEffect(() => {
    evaluationQueue.startAutoSync();
    const unsub = evaluationQueue.subscribeStatus((s) => {
      setQueueStatus(s);
      void loadPending();
      // A successful drain means the server now has records we don't show yet.
      if (s.pending === 0 && s.lastSuccessAt) void load();
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cultivarId]);

  // The toggle is only meaningful when there's a genuine mismatch to reconcile.
  const mixedHemispheres = useMemo(() => {
    const seen = new Set(evaluations.map((e) => e.hemisphere).filter(Boolean));
    return seen.size > 1;
  }, [evaluations]);

  // When normalising, order by the translated date — putting visits that sit at
  // the same point in the season next to each other is the entire purpose of
  // the toggle, and recorded-date order actively works against it.
  const displayed = useMemo(() => {
    if (!normaliseTo) return evaluations;
    return [...evaluations].sort((a, b) => {
      const ad = a.translated_date || a.evaluated_on;
      const bd = b.translated_date || b.evaluated_on;
      return ad < bd ? 1 : ad > bd ? -1 : 0;
    });
  }, [evaluations, normaliseTo]);

  const anyApproximate = useMemo(
    () => displayed.some((e) => e.translated_is_approximate),
    [displayed]
  );

  const handleResolveFollowup = async (id: number, status: 'completed' | 'dismissed') => {
    try {
      await cultivarService.resolveFollowup(id, status);
      setFollowups((prev) => prev.filter((f) => f.id !== id));
    } catch (e: any) {
      window.alert(e?.response?.data?.error?.message || e?.message || 'Failed');
    }
  };

  const startEvaluationFor = (followup: EvaluationFollowup) => {
    setFocusAttributeIds(followup.focus_attribute_ids || []);
    setFormOpen(true);
  };

  return (
    <>
      <Card>
        <CardHeader>
          {/* Wraps rather than running off the right edge at 320px. */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5" />Evaluations
            </CardTitle>
            <Button variant="primary" onClick={() => { setFocusAttributeIds([]); setFormOpen(true); }}>
              <Plus className="h-4 w-4 mr-1" /> New evaluation
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && <div className="mb-3 p-2 rounded bg-error-50 text-error-800 text-sm">{error}</div>}

          {/* Only worth offering once this cultivar actually has evaluations
              from both hemispheres — otherwise there's nothing to reconcile. */}
          {mixedHemispheres && (
            <div className="mb-4 rounded-lg border border-neutral-200 bg-neutral-50 p-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Globe className="h-4 w-4 text-neutral-500 shrink-0" />
                <span className="text-xs font-medium text-neutral-700">
                  Recorded in both hemispheres. Show dates as:
                </span>
                <div className="inline-flex rounded-md border border-neutral-300 bg-white overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setNormaliseTo(null)}
                    className={`px-2.5 py-1 text-xs ${normaliseTo === null ? 'bg-primary-50 text-primary-700' : 'text-neutral-600 hover:bg-neutral-50'}`}
                  >
                    As recorded
                  </button>
                  {(['S', 'N'] as Hemisphere[]).map((h) => (
                    <button
                      key={h}
                      type="button"
                      onClick={() => setNormaliseTo(h)}
                      className={`px-2.5 py-1 text-xs border-l border-neutral-300 ${normaliseTo === h ? 'bg-primary-50 text-primary-700' : 'text-neutral-600 hover:bg-neutral-50'}`}
                    >
                      {HEMISPHERE_LABELS[h]}
                    </button>
                  ))}
                </div>
              </div>
              {normaliseTo && (
                <p className="text-[11px] text-neutral-500 mt-2">
                  Showing each visit's equivalent {HEMISPHERE_LABELS[normaliseTo].toLowerCase()}-hemisphere date
                  alongside the date it was actually recorded.
                  {references?.[normaliseTo]
                    ? ` Anchored to the recorded harvest date of ${references[normaliseTo]}.`
                    : ' No harvest date recorded there yet, so translated dates are approximate six-month shifts.'}
                </p>
              )}
            </div>
          )}

          {/* ---------- Offline queue ---------- */}
          {pending.length > 0 && (
            <div className="mb-4 rounded-lg border border-warning-200 bg-warning-50 p-3">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 text-sm font-medium text-warning-800">
                  <CloudOff className="h-4 w-4" />
                  {pending.length} evaluation{pending.length > 1 ? 's' : ''} waiting to upload
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => evaluationQueue.flush()}
                  disabled={queueStatus.syncing}
                >
                  {queueStatus.syncing
                    ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                  Retry now
                </Button>
              </div>
              <ul className="space-y-1">
                {pending.map((p) => (
                  <li key={p.id} className="text-xs text-warning-800 flex items-center justify-between gap-2">
                    <span>
                      {p.evaluatedOn} — {p.valueCount} reading{p.valueCount === 1 ? '' : 's'}
                      {p.photoCount > 0 && `, ${p.photosUploaded}/${p.photoCount} photos uploaded`}
                    </span>
                    {p.attempts > 3 && (
                      <button
                        onClick={async () => {
                          if (window.confirm('Discard this queued evaluation? This cannot be undone.')) {
                            await evaluationQueue.discard(p.id);
                            await loadPending();
                          }
                        }}
                        className="text-error-600 hover:underline shrink-0"
                      >
                        Discard
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              {queueStatus.lastError && (
                <p className="mt-2 text-xs text-warning-700 flex items-start gap-1">
                  <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />
                  Last attempt failed: {queueStatus.lastError.message}
                </p>
              )}
            </div>
          )}

          {/* ---------- Due for re-evaluation ---------- */}
          {followups.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-neutral-900 mb-2 flex items-center gap-2">
                <CalendarClock className="h-4 w-4" /> Due for re-evaluation
              </h4>
              <div className="space-y-2">
                {followups.map((f) => (
                  <div
                    key={f.id}
                    className={`rounded-lg border p-3 ${
                      f.overdue ? 'border-error-200 bg-error-50' : 'border-neutral-200 bg-white'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className={`text-sm font-medium ${f.overdue ? 'text-error-800' : 'text-neutral-800'}`}>
                          {f.overdue ? 'Overdue' : 'Due'} {formatLocalDateInTz(f.due_date, orgTz)}
                        </p>
                        {f.note && <p className="text-xs text-neutral-600 mt-0.5">{f.note}</p>}
                        {f.assigned_to_name && (
                          <p className="text-xs text-neutral-500 mt-0.5">Assigned to {f.assigned_to_name}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="primary" size="sm" onClick={() => startEvaluationFor(f)}>
                          Evaluate
                        </Button>
                        <button
                          onClick={() => handleResolveFollowup(f.id, 'dismissed')}
                          className="text-neutral-400 hover:text-neutral-700 p-1.5"
                          aria-label="Dismiss reminder"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ---------- Recorded evaluations ---------- */}
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-neutral-500 py-6">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading evaluations…
            </div>
          ) : evaluations.length === 0 ? (
            <div className="text-center py-8">
              <ClipboardList className="h-8 w-8 text-neutral-300 mx-auto mb-2" />
              <p className="text-sm text-neutral-500">No evaluations recorded yet.</p>
              <p className="text-xs text-neutral-400 mt-1">
                Record a full orchard visit — readings, photos and follow-ups — in one form.
              </p>
            </div>
          ) : (
            <div className="border border-neutral-200 rounded-lg divide-y divide-neutral-100">
              {displayed.map((e) => {
                const position = seasonPositionLabel(e);
                return (
                  <div key={e.id} className="p-3 flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* The recorded date always leads — it's the fact.
                            The translated date is an interpretation and is
                            visually subordinate to it. */}
                        <span className="text-sm font-medium text-neutral-900">{formatLocalDateInTz(e.evaluated_on, orgTz)}</span>
                        {e.hemisphere && (
                          <span className="text-[10px] uppercase tracking-wide bg-neutral-100 text-neutral-600 rounded px-1.5 py-0.5">
                            {e.hemisphere}
                          </span>
                        )}
                        {e.translated_date && (
                          <span
                            className="text-xs text-primary-700 bg-primary-50 rounded px-1.5 py-0.5"
                            title={`Equivalent date in the ${HEMISPHERE_LABELS[normaliseTo!].toLowerCase()} hemisphere`}
                          >
                            ≈ {formatLocalDateInTz(e.translated_date, orgTz)}
                            {e.translated_is_approximate && '*'}
                          </span>
                        )}
                        {e.status === 'draft' && (
                          <span className="text-[10px] uppercase tracking-wide bg-neutral-200 text-neutral-700 rounded px-1.5 py-0.5">
                            Draft
                          </span>
                        )}
                        {e.season_stage && (
                          <span className="text-xs bg-primary-50 text-primary-700 rounded px-1.5 py-0.5">
                            {SEASON_STAGE_LABELS[e.season_stage]}
                          </span>
                        )}
                        {position && <span className="text-xs text-neutral-500">{position}</span>}
                      </div>

                      {e.summary && (
                        <p className="text-sm text-neutral-600 mt-1 line-clamp-2">{e.summary}</p>
                      )}

                      <div className="flex items-center gap-3 mt-1.5 text-xs text-neutral-500 flex-wrap">
                        <span>{e.value_count} reading{Number(e.value_count) === 1 ? '' : 's'}</span>
                        {Number(e.photo_count) > 0 && (
                          <span className="flex items-center gap-1">
                            <Camera className="h-3 w-3" />{e.photo_count}
                          </span>
                        )}
                        {(e.location_label || e.farm_name) && (
                          <span className="flex items-center gap-1 min-w-0">
                            <MapPin className="h-3 w-3 shrink-0" />
                            <span className="truncate">{e.location_label || e.farm_name}</span>
                          </span>
                        )}
                        {/* The region is what lets a maturity date on this visit
                            count towards the cultivar's harvest timing, so it is
                            worth seeing from the list rather than one level in. */}
                        {e.growing_region_name && (
                          <span className="truncate text-neutral-600">{e.growing_region_name}</span>
                        )}
                        {e.overall_score != null && (
                          <span className="font-medium text-neutral-700">{e.overall_score}/10</span>
                        )}
                        <span className="truncate">
                          {e.observer_user_name || e.observer_org_name}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-neutral-300 shrink-0 mt-1" />
                  </div>
                );
              })}
            </div>
          )}

          {anyApproximate && (
            <p className="mt-2 text-[11px] text-neutral-500">
              * Approximate — translated by a flat six-month shift because no harvest
              date has been recorded in that hemisphere for this cultivar.
            </p>
          )}
        </CardContent>
      </Card>

      <EvaluationForm
        open={formOpen}
        cultivarId={cultivarId}
        cultivarName={cultivarName}
        focusAttributeIds={focusAttributeIds}
        onClose={() => setFormOpen(false)}
        onSaved={() => { void load(); }}
      />
    </>
  );
}

export default EvaluationsPanel;
